import React, { useState, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { cn } from '../../lib/utils';
import { DateRange } from '../../services/ppcFarmCostService';

interface MailingCostDateRangeSelectorProps {
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  className?: string;
}

const DATE_RANGE_PRESETS = [
  { label: 'Last Quarter', value: 'Last Quarter' },
  { label: 'This Year', value: 'This Year' },
  { label: 'All Time', value: 'All Time' },
  { label: 'Custom Range', value: 'Custom Range' }
];

export const MailingCostDateRangeSelector: React.FC<MailingCostDateRangeSelectorProps> = ({
  dateRange,
  onDateRangeChange,
  className
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string>('All Time');
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState<DateRange>(dateRange);
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  
  // Debug logging
  console.log('Current tempDateRange:', tempDateRange);

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset);
    
    if (preset === 'Custom Range') {
      setIsCustomRange(true);
      return;
    }

    setIsCustomRange(false);
    
    // Get preset date ranges
    const presets = getDateRangePresets();
    const newDateRange = presets[preset];
    
    if (newDateRange) {
      onDateRangeChange(newDateRange);
      setTempDateRange(newDateRange);
    }
    
    setIsPopoverOpen(false);
  };

  const handleCustomDateApply = () => {
    onDateRangeChange(tempDateRange);
    setIsPopoverOpen(false);
  };

  const handleCustomDateCancel = () => {
    setTempDateRange(dateRange);
    setIsCustomRange(false);
    setIsPopoverOpen(false);
  };

  const formatDateRange = (range: DateRange): string => {
    if (!range.start && !range.end) {
      return 'All Time';
    }
    
    const formatDate = (date: Date | null) => {
      if (!date) return '';
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    };

    if (range.start && range.end) {
      return `${formatDate(range.start)} - ${formatDate(range.end)}`;
    } else if (range.start) {
      return `From ${formatDate(range.start)}`;
    } else if (range.end) {
      return `Until ${formatDate(range.end)}`;
    }
    
    return 'Select Date Range';
  };

  const getDateRangePresets = (): { [key: string]: DateRange } => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentQuarter = Math.floor(now.getMonth() / 3);
    
    // Last Quarter calculation
    const lastQuarterStart = new Date(currentYear, (currentQuarter - 1) * 3, 1);
    const lastQuarterEnd = new Date(currentYear, currentQuarter * 3, 0);
    
    // This Year calculation
    const thisYearStart = new Date(currentYear, 0, 1);
    const thisYearEnd = new Date(currentYear, 11, 31);

    return {
      'Last Quarter': {
        start: lastQuarterStart,
        end: lastQuarterEnd
      },
      'This Year': {
        start: thisYearStart,
        end: thisYearEnd
      },
      'All Time': {
        start: null,
        end: null
      }
    };
  };

  return (
    <div className={cn('flex items-center space-x-4', className)}>
      <div className="flex items-center space-x-2">
        <Calendar className="h-5 w-5 text-gray-500" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Date Range:
        </span>
      </div>
      
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-64 justify-between text-left font-normal"
          >
            <span className="truncate">
              {formatDateRange(dateRange)}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className={cn("p-0", isCustomRange ? "w-[640px]" : "w-80")} align="start">
          <div className="p-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-gray-900 dark:text-white">
                Select Date Range
              </h4>
              
              {/* Preset Options */}
              <div className="space-y-1">
                {DATE_RANGE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => handlePresetChange(preset.value)}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                      selectedPreset === preset.value
                        ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-900 dark:text-yellow-100'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Range Picker */}
              {isCustomRange && (
                <div className="mt-4 pt-4 border-t">
                  <div className="space-y-4">
                    {/* Single calendar with range selection */}
                    <div className="flex flex-col items-center">
                      <div className="mb-4 text-center">
                        <div className="flex gap-2 justify-center mb-2">
                          <button
                            onClick={() => setSelectingStartDate(true)}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                              selectingStartDate
                                ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-900 dark:text-yellow-100'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            Select Start Date
                          </button>
                          <button
                            onClick={() => setSelectingStartDate(false)}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                              !selectingStartDate
                                ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-900 dark:text-yellow-100'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            Select End Date
                          </button>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {selectingStartDate ? 'Click a date to set start date' : 'Click a date to set end date'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Start: {tempDateRange.start ? tempDateRange.start.toLocaleDateString() : 'Not set'} |
                          End: {tempDateRange.end ? tempDateRange.end.toLocaleDateString() : 'Not set'}
                        </div>
                      </div>
                      
                      <CalendarComponent
                        mode="single"
                        selected={selectingStartDate ? (tempDateRange.start || undefined) : (tempDateRange.end || undefined)}
                        onSelect={(date: Date | undefined) => {
                          console.log(`${selectingStartDate ? 'Start' : 'End'} date selected:`, date);
                          if (selectingStartDate) {
                            setTempDateRange(prev => ({ ...prev, start: date || null }));
                            // Auto-switch to end date selection after start date is selected
                            if (date) {
                              setSelectingStartDate(false);
                            }
                          } else {
                            setTempDateRange(prev => ({ ...prev, end: date || null }));
                          }
                        }}
                        className="rounded-md border"
                        initialFocus
                      />
                    </div>
                    
                    <div className="flex space-x-2 pt-2">
                      <Button
                        size="sm"
                        onClick={handleCustomDateApply}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white"
                      >
                        Apply
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCustomDateCancel}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MailingCostDateRangeSelector;